/**
 * Fabrique d'éléments DOM.
 *
 * Tout passe par `document.createElement` et `textContent` : aucune
 * concaténation de HTML, donc aucune injection possible depuis une valeur
 * venue de l'API, et aucun besoin de relâcher la politique de sécurité de
 * contenu.
 */

type Enfant = Node | string | number | null | undefined | false;

export interface Attributs {
  readonly [cle: string]: string | number | boolean | null | undefined | ((e: Event) => void);
}

function appliquer(el: HTMLElement | SVGElement, attrs: Attributs): void {
  for (const [cle, valeur] of Object.entries(attrs)) {
    if (valeur === null || valeur === undefined || valeur === false) continue;
    if (cle === 'classe') {
      el.setAttribute('class', String(valeur));
    } else if (cle.startsWith('sur') && typeof valeur === 'function') {
      el.addEventListener(cle.slice(3).toLowerCase(), valeur);
    } else if (cle.startsWith('--')) {
      el.style.setProperty(cle, String(valeur));
    } else if (valeur === true) {
      el.setAttribute(cle, '');
    } else {
      el.setAttribute(cle, String(valeur));
    }
  }
}

function ajouter(el: Node, enfants: readonly Enfant[]): void {
  for (const enfant of enfants) {
    if (enfant === null || enfant === undefined || enfant === false) continue;
    if (typeof enfant === 'number') {
      el.appendChild(document.createTextNode(String(enfant)));
      continue;
    }
    if (typeof enfant === 'string') {
      // `**gras**` est rendu partout, pas seulement là où l'appelant a pensé à
      // passer par `richeTexte`. Sans cela les astérisques s'affichaient telles
      // quelles dans les paragraphes de lecture, sur presque tous les écrans.
      el.appendChild(enfant.includes('**') ? richeTexte(enfant) : document.createTextNode(enfant));
      continue;
    }
    el.appendChild(enfant);
  }
}

/** Élément HTML. `h('div', { classe: 'x' }, 'texte')`. */
export function h<K extends keyof HTMLElementTagNameMap>(
  balise: K,
  attrs: Attributs = {},
  ...enfants: Enfant[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(balise);
  appliquer(el, attrs);
  ajouter(el, enfants);
  return el;
}

/** Élément SVG. */
export function s(balise: string, attrs: Attributs = {}, ...enfants: Enfant[]): SVGElement {
  const el = document.createElementNS('http://www.w3.org/2000/svg', balise);
  appliquer(el, attrs);
  ajouter(el, enfants);
  return el;
}

/** Fragment, pour renvoyer plusieurs nœuds. */
export function frag(...enfants: Enfant[]): DocumentFragment {
  const f = document.createDocumentFragment();
  ajouter(f, enfants);
  return f;
}

/** Vide un conteneur sans passer par innerHTML. */
export function vider(el: Element): void {
  while (el.firstChild) el.removeChild(el.firstChild);
}

/**
 * Rend un texte contenant des passages en gras, décrits par `**…**`.
 * Évite `innerHTML` tout en gardant les libellés lisibles dans le code.
 */
export function richeTexte(texte: string): DocumentFragment {
  const f = document.createDocumentFragment();
  for (const [i, part] of texte.split('**').entries()) {
    if (part === '') continue;
    f.appendChild(i % 2 === 1 ? h('strong', {}, part) : document.createTextNode(part));
  }
  return f;
}
